import React, { useState } from 'react';
import { Salad, UtensilsCrossed, Pizza, Beef, ChefHat, Wheat, Baby, Soup } from 'lucide-react';
import { menuCategories } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { FadeIn } from './AnimatedComponents';

const iconMap = {
  Salad,
  UtensilsCrossed,
  Pizza,
  Beef,
  ChefHat,
  Wheat,
  Baby,
  Soup
};

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t, language } = useLanguage();

  const activeMenu = menuCategories.find(cat => cat.id === activeCategory);

  const getCategoryName = (cat) => {
    if (language === 'de' && cat.nameDE) return cat.nameDE;
    if (language === 'fr' && cat.nameFR) return cat.nameFR;
    if (language === 'en' && cat.nameEN) return cat.nameEN;
    return cat.name;
  };

  const getDescription = (item) => {
    if (language === 'de') return item.descDE;
    if (language === 'fr') return item.descFR;
    if (language === 'en') return item.descEN;
    return item.descIT;
  };

  const handleCategoryChange = (id) => {
    if (id !== activeCategory) {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveCategory(id);
        setIsAnimating(false);
      }, 150);
    }
  };

  return (
    <section id="menu" className="py-24 bg-stone-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d97706' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <FadeIn className="text-center mb-12">
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('menu.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('menu.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />
        </FadeIn>

        {/* Category Tabs */}
        <FadeIn delay={200} className="flex flex-wrap justify-center gap-2 mb-12">
          {menuCategories.map((category) => {
            const IconComponent = iconMap[category.icon];
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 text-sm ripple ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg shadow-amber-900/30 scale-105'
                    : 'bg-stone-800/50 text-amber-100/70 hover:bg-stone-800 hover:text-amber-100 hover:scale-105'
                }`}
              >
                {IconComponent && <IconComponent className={`w-4 h-4 transition-transform duration-300 ${activeCategory === category.id ? 'rotate-12' : ''}`} />}
                <span className="font-medium">{getCategoryName(category)}</span>
              </button>
            );
          })}
        </FadeIn>

        {/* Menu Items Grid */}
        <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          {activeMenu?.items.map((item, index) => (
            <div
              key={index}
              className="group bg-stone-950/50 backdrop-blur-sm border border-stone-800 hover:border-amber-700/50 rounded-xl p-4 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg hover:shadow-amber-900/20"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white group-hover:text-amber-200 transition-colors duration-300">
                    {item.name}
                  </h3>
                  {getDescription(item) && (
                    <p className="text-amber-100/50 mt-1 text-xs leading-relaxed line-clamp-2 group-hover:text-amber-100/70 transition-colors">
                      {getDescription(item)}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-amber-400 font-semibold text-sm whitespace-nowrap">
                    CHF {item.price}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Menu Note */}
        <FadeIn delay={300} className="text-center mt-10 space-y-2 max-w-3xl mx-auto">
          <p className="text-amber-100/50 text-sm">
            {t('menu.note')}
          </p>
          <p className="text-amber-400/70 text-sm">
            🍕 {t('menu.halfPizza')}
          </p>
          <p className="text-amber-100/40 text-xs">
            {t('menu.sides')}
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

export default Menu;
